

























































































































import {
	computed,
	defineComponent,
	PropType,
	Ref,
	ref,
	watch,
} from '@vue/composition-api';
import { StepContentTypeEnum, UpsertStepRequest } from '@front-app/api-generated/learning-management-api';
import { useFocusFirstInvalidField } from '@front-app/hooks/useFocusFirstInvalidField';
import { required, requiredIfBoth } from '@front-app/components/form/validation/validators';
import { v } from '@front-app/components/form/validation/useValidation';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import { Size } from '@front-app/components/constants';
import HelpTooltipBlock from '@front-app/components/dropdowns/HelpTooltipBlock.vue';
import TrForm from '@front-app/components/form/Form.vue';
import FormSection from '@front-app/components/form/FormSection.vue';
import FormInput from '@front-app/components/form/FormInput.vue';
import FormLabel from '@front-app/components/form/FormLabel.vue';
import FormError from '@front-app/components/form/FormError.vue';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import {
	LearningProgram,
	LearningStep,
	NewLearningStepContentType,
} from '../Model';
import StepTypePicker from './StepEditTypePicker.vue';
import SelectSpeakers from './inputs/SelectSpeakers.vue';
import { useSelectSpeakers } from './inputs/selectSpeakers';

const STEP_ID_LABEL = {
	[StepContentTypeEnum.Survey]: 'lms.admin.forms.step-survey-id-label',
	[StepContentTypeEnum.Youtube]: 'lms.admin.forms.step-youtube-id-label',
	[StepContentTypeEnum.Vimeo]: 'lms.admin.forms.step-vimeo-url-label',
};

export default defineComponent({
	components: {
		CtaButton,
		TrForm,
		FormSection,
		FormInput,
		FormLabel,
		FormError,
		HelpTooltipBlock,
		StepTypePicker,
		SelectSpeakers,
	},
	props: {
		title: {
			type: String,
			required: true,
		},
		step: {
			type: Object as PropType<LearningStep>,
			required: true,
		},
		program: {
			type: Object as PropType<LearningProgram>,
			required: true,
		},
		groupId: {
			type: String,
			required: true,
		},
		isLoading: Boolean,
		isError: Boolean,
	},
	setup(props, ctx) {
		// Fields
		const name = ref('');
		watch(() => props.step.name, (curr) => {
			name.value = curr;
		}, { immediate: true });

		const identifier = ref('');
		watch(() => props.step.identifier, (curr) => {
			identifier.value = curr ?? '';
		}, { immediate: true });

		const {
			speakerIds,
			removeSpeaker,
			selectSpeaker,
		} = useSelectSpeakers(() => props.step.speakers);

		const contentType: Ref<NewLearningStepContentType> = ref('none');
		watch(() => props.step.content.type, (curr) => {
			contentType.value = curr;
		}, { immediate: true });

		const contentId = ref('');
		watch(() => props.step.content.id, (curr) => {
			contentId.value = curr;
		}, { immediate: true });

		const hasNoneType = computed(() => contentType.value === 'none');
		const contentIdLabel = computed(() => {
			if (contentType.value !== 'none') {
				return STEP_ID_LABEL[contentType.value];
			}
			return '';
		});

		// Validation
		const {
			focusFirstInvalid,
		} = useFocusFirstInvalidField({ containerSelector: '.edit-part-form' });
		const validation = v({
			name: {
				value: name,
				rules: [required()],
			},
			contentId: {
				value: contentId,
				rules: [requiredIfBoth(() => contentType.value !== 'none')],
			},
		});

		// Submission
		const updatePart = async () => {
			validation.validate();

			if (!validation.isValid.value) {
				focusFirstInvalid();
				return;
			}
			if (contentType.value === 'none') {
				return;
			}
			const payload: UpsertStepRequest = {
				name: name.value,
				identifier: identifier.value,
				speakers: speakerIds.value,
				content: {
					type: contentType.value,
					id: contentId.value.trim(),
				},
			};

			ctx.emit('submit', payload);
		};

		return {
			name,
			identifier,
			speakerIds,
			validation,
			contentType,
			contentId,
			hasNoneType,
			contentIdLabel,
			updatePart,
			StepContentTypeEnum,
			selectSpeaker,
			removeSpeaker,
			// Util
			submitButtonPalette: ButtonPalettes.PRIMARY,
			submitButtonsize: Size.LARGE,
		};
	},
});
